/**
 * MiniProjectMissionTab — Task #230.
 *
 * Body for the Mission sub-tab inside MiniProjectKpiNavigation. Order
 * locked by Skip 2026-05-27 (revised pass):
 *
 *   1. Mission aggregate one-liner (the "what's the headline" signal)
 *   2. HSE row (Health · Safety · Environment — core to mission)
 *   3. Goals section (the work being tracked, Lead-editable)
 *   4. History heatmap (per-goal assessment timeline)
 *
 * The HSE row sits BETWEEN aggregate and Goals — not at the absolute
 * top — because the aggregate is the headline status; HSE follows
 * immediately as a non-negotiable companion (CLAUDE.md HSE principle).
 * History anchors the bottom — it's the longest visual element and
 * reads as supporting context for the editable surfaces above it.
 *
 * ── HSE row ─────────────────────────────────────────────────────
 *
 * Per the locked CLAUDE.md HSE principle, HSE is part of mission, not
 * ops paperwork. The platform serves teenagers, parents, and teachers
 * — the pedagogical message is that Health, Safety, and Environment
 * are core to any project.
 *
 * ⚠ Placeholder implementation. FP runs a full HSE pipeline (auto-
 * provisioned `kpi_config` rows + `project_issues` extension +
 * `computeHseBundle()` + `HSERow` / `HSECard` / `IncidentDrillDown`
 * / `EditIncidentModal`). MP has none of that yet — `kpi_config`
 * was made polymorphic by DEV-1075 but no migration provisions the
 * 3 HSE cards for mini-projects, and `project_issues` is still
 * project-scoped. So this row renders an honest static placeholder
 * that surfaces the principle without faking reporting functionality.
 * The real `HSERow` from FP drops in once the backend pipeline is
 * built for MP (filed as a separate Jira follow-up).
 *
 * ── Aggregate ───────────────────────────────────────────────────
 *
 * One-liner reads the latest filed status's goal assessments and
 * prints counts ("5 goals · 3 on track · 1 behind") with each count
 * colored to its state.
 *
 * ── Goals ───────────────────────────────────────────────────────
 *
 * Flat list (no completed/active split, parallel to FP). Delegates
 * to the existing GoalsSection component which owns its add / edit /
 * data-entry / detail trays. Complete = currentValue >= targetValue
 * per Skip 2026-05-27 — recorded here as the canonical definition;
 * the existing GoalCard already shows current/target so no separate
 * complete badge is added in this pass.
 *
 * ── History (heatmap) ───────────────────────────────────────────
 *
 * Lifted from MissionModal: rows = goals, columns = filed statuses
 * (oldest → newest), each cell colored by that status's per-goal
 * assessment. Modal chrome (header, scrim, close button) is dropped.
 * Legend kept per Skip 2026-05-27 — the cells are historical data,
 * the legend explains how to read them.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchMiniProjectStatus,
  type MiniProjectStatus,
  type MiniProjectStatusSummary,
  type MiniProjectAssessment,
} from "../../api/miniProjectStatuses";
import type { MiniProject } from "../../api/miniProjects";
import type { MiniProjectGoalView } from "../../Data/MiniProjectConfig/miniProjectGoalView";
import {
  aggregateAssessmentsToColor,
  isQualitativeAssessment,
  type QualitativeAssessment,
} from "../../kpi-engine/qualitativeLadder";
import GoalsSection from "../../Pages/MiniProjectDetail/sections/GoalsSection";
import { HSERow } from "../HSERow/HSERow";
import { ReportIncidentModal } from "../ReportIncidentModal/ReportIncidentModal";
import { EditIncidentModal } from "../EditIncidentModal/EditIncidentModal";
import { IncidentDrillDown } from "../IncidentDrillDown/IncidentDrillDown";
import { computeHseBundle } from "../../kpi-engine/hse";
import type { HseCategory } from "../../kpi-engine/missionTypes/types";
import { MP_HSE_CONFIGS } from "../../Data/MiniProjectConfig/miniProjectHseConfigs";
import { useHseMiniProjectIncidents } from "../../api/hseIncidents";
import { getCurrentUserGuid } from "../../api/client";
import styles from "./MiniProjectMissionTab.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  miniProject: MiniProject;
  statusSummaries: MiniProjectStatusSummary[];
  goals: MiniProjectGoalView[];
  isLead: boolean;
  onGoalsMutated: () => void;
  addingGoal: boolean;
  onOpenAddGoal: () => void;
  onCloseAddGoal: () => void;
}

// Per-cell heatmap class. Mirrors MissionModal's mapping so the visual
// language stays consistent between the (future-deprecated) modal and
// this inline surface during the transition window.
const ASSESSMENT_CELL_CLASS: Record<MiniProjectAssessment, string> = {
  ahead: "cellSuccess",
  on_track: "cellSuccess",
  behind: "cellWarning",
  blocked: "cellDanger",
  done: "cellInfo",
};

const ASSESSMENT_LABEL_KEY: Record<MiniProjectAssessment, string> = {
  ahead: "miniProject.kpiModals.mission.assessment.ahead",
  on_track: "miniProject.kpiModals.mission.assessment.onTrack",
  behind: "miniProject.kpiModals.mission.assessment.behind",
  blocked: "miniProject.kpiModals.mission.assessment.blocked",
  done: "miniProject.kpiModals.mission.assessment.done",
};

/* ── Helpers ───────────────────────────────────────────────────── */

function formatShortDate(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatMonth(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
    });
  } catch {
    return "";
  }
}

function formatDay(iso: string): string {
  try {
    const parts = iso.split("-").map((s) => parseInt(s, 10));
    return String(parts[2] || "");
  } catch {
    return iso;
  }
}

/* ── Aggregate one-liner ──────────────────────────────────────── */

function computeAggregate(
  latest: MiniProjectStatus | null,
  goalCount: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): {
  goalCount: number;
  ahead: number;
  onTrack: number;
  behind: number;
  blocked: number;
  done: number;
  overallColor: "info" | "success" | "warning" | "danger" | "muted";
  emptyLabel: string;
} {
  if (!latest || latest.goalAssessments.length === 0) {
    return {
      goalCount,
      ahead: 0,
      onTrack: 0,
      behind: 0,
      blocked: 0,
      done: 0,
      overallColor: "muted",
      emptyLabel: t("miniProject.mission.aggregate.noAssessments", {
        defaultValue: "No assessments yet",
      }),
    };
  }
  const valid: QualitativeAssessment[] = latest.goalAssessments
    .map((a) => a.assessment)
    .filter(isQualitativeAssessment);
  const counts = { ahead: 0, on_track: 0, behind: 0, blocked: 0, done: 0 };
  for (const a of valid) counts[a]++;
  const { colorValue, allDone } = aggregateAssessmentsToColor(valid);
  let overallColor: "info" | "success" | "warning" | "danger" | "muted" = "muted";
  if (colorValue === 1) overallColor = "danger";
  else if (colorValue === 2) overallColor = "warning";
  else if (colorValue === 3 && allDone) overallColor = "info";
  else if (colorValue === 3) overallColor = "success";
  return {
    goalCount,
    ahead: counts.ahead,
    onTrack: counts.on_track,
    behind: counts.behind,
    blocked: counts.blocked,
    done: counts.done,
    overallColor,
    emptyLabel: "",
  };
}

/* ── Component ─────────────────────────────────────────────────── */

export default function MiniProjectMissionTab({
  miniProject,
  statusSummaries,
  goals,
  isLead,
  onGoalsMutated,
  addingGoal,
  onOpenAddGoal,
  onCloseAddGoal,
}: Props) {
  const { t } = useAppTranslation("pages");

  // Heatmap data — fan out one fetch per filed status to get full
  // payloads with goalAssessments. Same pattern MissionModal uses;
  // capped by the server's summary limit (200) so this stays bounded.
  const [fullStatuses, setFullStatuses] = useState<MiniProjectStatus[]>([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  useEffect(() => {
    if (statusSummaries.length === 0) {
      setFullStatuses([]);
      return;
    }
    let cancelled = false;
    setHeatmapLoading(true);
    Promise.all(
      statusSummaries.map((s) =>
        fetchMiniProjectStatus(miniProject.miniProjectGuid, s.statusGuid),
      ),
    )
      .then((statuses) => {
        if (!cancelled) setFullStatuses(statuses);
      })
      .catch(() => {
        // Soft-fail — heatmap shows an empty state rather than blocking
        // the rest of the tab.
        if (!cancelled) setFullStatuses([]);
      })
      .finally(() => {
        if (!cancelled) setHeatmapLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [miniProject.miniProjectGuid, statusSummaries]);

  // Chronological order — oldest left, newest right.
  const chrono = useMemo(() => [...fullStatuses].reverse(), [fullStatuses]);

  // Aggregate uses the LATEST full status (newest reportingDate).
  const latestFull = fullStatuses[0] || null;
  const agg = computeAggregate(latestFull, goals.length, t);

  // ── HSE (DEV-1141) ─────────────────────────────────────────────
  // Real HSE pipeline, mirroring the full-project Mission tab. Incidents
  // come from the polymorphic mini-project route; the bundle is scored by
  // the same engine the FP side uses. excludeHse() stays the enforcement
  // mechanism on the *mission* consumers (status writer, aggregate) — those
  // never see incident_count; this HSE row is the onlyHse() surface.
  const { incidents: hseIncidents } = useHseMiniProjectIncidents(
    miniProject.miniProjectGuid,
  );
  const hseBundle = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return computeHseBundle(MP_HSE_CONFIGS, hseIncidents, today);
  }, [hseIncidents]);
  // The Mission + Summary KPI nav-tab colors are floor-capped by this
  // bundle's worst_color upstream (ProgressTab → MiniProjectKpiNavigation),
  // which also renders the FP adjustment dot on the capped tabs. The
  // SCORE/counts are never capped — color only.

  // Current user — used by EditIncidentModal to decide whether to show the
  // sysadmin-only Delete affordance (backend enforces the rule regardless).
  const currentUserGuid = getCurrentUserGuid();

  // Report / edit modal state, parallel to the FP Mission tab.
  const [hseReportOpen, setHseReportOpen] = useState(false);
  const [hseReportCategory, setHseReportCategory] = useState<HseCategory>("safety");
  const [editingIncidentId, setEditingIncidentId] = useState<string | null>(null);

  // (goal, status) assessment lookup keyed by guid-pair for O(1) cell
  // resolution. Goals deleted between filings are silently skipped.
  const cellByGoalAndStatus = useMemo(() => {
    const m = new Map<string, MiniProjectAssessment>();
    const activeGoalGuids = new Set(goals.map((g) => g.goalGuid.toUpperCase()));
    for (const s of chrono) {
      for (const a of s.goalAssessments) {
        const key = a.goalGuid.toUpperCase();
        if (!activeGoalGuids.has(key)) continue;
        m.set(`${key}::${s.statusGuid}`, a.assessment);
      }
    }
    return m;
  }, [goals, chrono]);

  // Latest-status per-goal assessment map. Drives the progress-bar
  // color on each MiniProjectGoalCard in the Goals section below
  // (Skip 2026-05-27 — bar should reflect the Lead's most recent
  // qualitative judgement, not just whether the number hit target).
  // Keyed on uppercased goalGuid to dodge any GUID case mismatches.
  const latestAssessments = useMemo(() => {
    const m = new Map<string, MiniProjectAssessment>();
    if (!latestFull) return m;
    for (const a of latestFull.goalAssessments) {
      m.set(a.goalGuid.toUpperCase(), a.assessment);
    }
    return m;
  }, [latestFull]);

  // Heatmap grid template — name column + N data columns.
  const gridStyle: React.CSSProperties = {
    gridTemplateColumns: `var(--mt-name-col, 160px) repeat(${chrono.length}, var(--mt-cell, 24px))`,
  };

  return (
    <div className={styles.root}>
      {/* ── Mission aggregate ─────────────────────────────────── */}
      <div className={styles.aggregateCard}>
        <div className={styles.eyebrow}>
          {t("miniProject.mission.aggregate.eyebrow", { defaultValue: "Mission" })}
        </div>
        {agg.emptyLabel ? (
          <div className={styles.aggregateEmpty}>{agg.emptyLabel}</div>
        ) : (
          <div className={styles.aggregateLine}>
            <span className={styles.aggregateGoalCount}>
              {t("miniProject.mission.aggregate.goalCount", {
                count: agg.goalCount,
                defaultValue: `${agg.goalCount} goals`,
              })}
            </span>
            {agg.ahead > 0 && (
              <span className={`${styles.aggregateBit} ${styles.aggSuccess}`}>
                · {agg.ahead} {t("miniProject.kpiModals.mission.assessment.ahead", { defaultValue: "ahead" })}
              </span>
            )}
            {agg.onTrack > 0 && (
              <span className={`${styles.aggregateBit} ${styles.aggSuccess}`}>
                · {agg.onTrack} {t("miniProject.kpiModals.mission.assessment.onTrack", { defaultValue: "on track" })}
              </span>
            )}
            {agg.behind > 0 && (
              <span className={`${styles.aggregateBit} ${styles.aggWarning}`}>
                · {agg.behind} {t("miniProject.kpiModals.mission.assessment.behind", { defaultValue: "behind" })}
              </span>
            )}
            {agg.blocked > 0 && (
              <span className={`${styles.aggregateBit} ${styles.aggDanger}`}>
                · {agg.blocked} {t("miniProject.kpiModals.mission.assessment.blocked", { defaultValue: "blocked" })}
              </span>
            )}
            {agg.done > 0 && (
              <span className={`${styles.aggregateBit} ${styles.aggInfo}`}>
                · {agg.done} {t("miniProject.kpiModals.mission.assessment.done", { defaultValue: "done" })}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── HSE row (DEV-1141) ─────────────────────────────────
          Real Health/Safety/Environment cards, scored by the same
          engine as full projects (computeHseBundle). Per the locked
          CLAUDE.md HSE principle, HSE sits high on Mission — second
          only to the aggregate, above Goals and the history heatmap.
          This is the onlyHse() surface; the mission aggregate above
          stays excludeHse() (it reads status assessments, which never
          carry incident_count). Renders nothing if no HSE cards exist
          (e.g. a mini not yet backfilled). */}
      <HSERow
        bundle={hseBundle}
        onReportIncident={(category) => {
          setHseReportCategory(category);
          setHseReportOpen(true);
        }}
        renderDrillDown={(card, onDrillClose) => (
          <IncidentDrillDown
            card={card}
            projectGuid=""
            miniProjectGuid={miniProject.miniProjectGuid}
            onClose={onDrillClose}
            onIncidentClick={(issueId) => setEditingIncidentId(issueId)}
          />
        )}
      />

      {/* ── Goals section ────────────────────────────────────── */}
      <div className={styles.goalsCard}>
        <div className={styles.goalsHeader}>
          <div className={styles.eyebrow}>
            {t("miniProject.progress.section.goals", { defaultValue: "Goals" })}
          </div>
          {isLead && (
            <button
              type="button"
              onClick={onOpenAddGoal}
              aria-label={t("miniProject.progress.section.addGoal", {
                defaultValue: "Add goal",
              })}
              className={styles.addBtn}
            >
              +
            </button>
          )}
        </div>
        <GoalsSection
          miniProjectGuid={miniProject.miniProjectGuid}
          goals={goals}
          isLead={isLead}
          onMutated={onGoalsMutated}
          addOpen={addingGoal}
          onCloseAdd={onCloseAddGoal}
          latestAssessments={latestAssessments}
        />
      </div>

      {/* ── History (per-goal assessment heatmap) ────────────── */}
      {goals.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyTitle}>
            {t("miniProject.kpiModals.mission.empty.noGoalsTitle")}
          </div>
          <div className={styles.emptyHint}>
            {isLead
              ? t("miniProject.kpiModals.mission.empty.noGoalsLeadHint")
              : t("miniProject.kpiModals.mission.empty.noGoalsPublicHint")}
          </div>
        </div>
      ) : statusSummaries.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyTitle}>
            {t("miniProject.kpiModals.mission.empty.noStatusTitle")}
          </div>
          <div className={styles.emptyHint}>
            {isLead
              ? t("miniProject.kpiModals.mission.empty.noStatusLeadHint")
              : t("miniProject.kpiModals.mission.empty.noStatusPublicHint")}
          </div>
        </div>
      ) : (
        <div className={styles.heatmapCard}>
          <div className={styles.heatmapFrame}>
            <div className={styles.heatmap} style={gridStyle}>
              {/* corner cell — empty, anchors the goal-name column */}
              <div className={styles.cornerCell} />

              {/* date header row */}
              {chrono.map((s) => (
                <div key={`d-${s.statusGuid}`} className={styles.dateCell}>
                  <span className={styles.dateMonth}>
                    {formatMonth(s.reportingDate)}
                  </span>
                  <span className={styles.dateDay}>
                    {formatDay(s.reportingDate)}
                  </span>
                </div>
              ))}

              {/* one row per goal */}
              {goals.map((g) => {
                const currentValue = Number(g.currentValue || 0);
                const targetValue = g.targetValue ? Number(g.targetValue) : null;
                const goalKey = g.goalGuid.toUpperCase();
                return (
                  <React.Fragment key={g.goalGuid}>
                    <div className={styles.nameCell}>
                      <div className={styles.nameMain}>{g.nameKey}</div>
                      <div className={styles.nameProgress}>
                        {targetValue !== null
                          ? t("miniProject.kpiModals.mission.goal.progressOf", {
                              current: currentValue,
                              target: targetValue,
                              unit: g.unitKey || "",
                            })
                          : t("miniProject.kpiModals.mission.goal.progressNoTarget", {
                              current: currentValue,
                              unit: g.unitKey || "",
                            })}
                      </div>
                    </div>
                    {chrono.map((s) => {
                      const assessment = cellByGoalAndStatus.get(
                        `${goalKey}::${s.statusGuid}`,
                      );
                      if (!assessment) {
                        return (
                          <div
                            key={`${g.goalGuid}-${s.statusGuid}`}
                            className={styles.cellEmpty}
                            title={t(
                              "miniProject.kpiModals.mission.cellEmptyTitle",
                              { date: formatShortDate(s.reportingDate) },
                            )}
                          />
                        );
                      }
                      const assessmentLabel = t(ASSESSMENT_LABEL_KEY[assessment]);
                      return (
                        <div
                          key={`${g.goalGuid}-${s.statusGuid}`}
                          className={`${styles.cell} ${styles[ASSESSMENT_CELL_CLASS[assessment]]}`}
                          title={t("miniProject.kpiModals.mission.chipTitle", {
                            date: formatShortDate(s.reportingDate),
                            assessment: assessmentLabel,
                          })}
                          aria-label={t("miniProject.kpiModals.mission.chipAria", {
                            date: s.reportingDate,
                            assessment: assessmentLabel,
                          })}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {heatmapLoading && (
            <div className={styles.heatmapLoading}>
              {t("miniProject.kpiModals.mission.loading", { defaultValue: "Loading…" })}
            </div>
          )}

          {/* Legend kept per Skip 2026-05-27: heatmap is historical data,
              users need to know what the cell colors mean. */}
          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.cellSuccess}`} />
              {t("miniProject.kpiModals.mission.legend.aheadOnTrack")}
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.cellWarning}`} />
              {t("miniProject.kpiModals.mission.legend.behind")}
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.cellDanger}`} />
              {t("miniProject.kpiModals.mission.legend.blocked")}
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.cellInfo}`} />
              {t("miniProject.kpiModals.mission.legend.done")}
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.cellEmpty}`} />
              {t("miniProject.kpiModals.mission.legend.noAssessment")}
            </span>
          </div>
        </div>
      )}

      {/* ── HSE modals (DEV-1141) ──────────────────────────────
          Report opens from "+ Report" on any HSE card; Edit opens from
          an incident row in the drill-down. Both routed to the mini via
          miniProjectGuid — the same reusable FP components, owner-switched. */}
      <ReportIncidentModal
        open={hseReportOpen}
        projectGuid=""
        miniProjectGuid={miniProject.miniProjectGuid}
        projectName={miniProject.title || undefined}
        initialCategory={hseReportCategory}
        onClose={() => setHseReportOpen(false)}
      />
      <EditIncidentModal
        issueId={editingIncidentId}
        projectGuid=""
        miniProjectGuid={miniProject.miniProjectGuid}
        projectName={miniProject.title || undefined}
        onClose={() => setEditingIncidentId(null)}
        currentUserGuid={currentUserGuid}
        onDeleted={() => setEditingIncidentId(null)}
      />

    </div>
  );
}
